import { emptySplitApi } from "./empty-split-api";
import { Practice, PracticeBillingSettings } from "./types/clinics.type";
import {
  AccessiblePractice,
  AccessiblePracticesFilters
} from "./types/practices.type";

export const practicesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    updatePractice: builder.mutation<
      Practice,
      Partial<Practice> & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/practices/${id}/`,
        method: "PATCH",
        body: body
      })
    }),
    getPracticeBillingSettings: builder.query<PracticeBillingSettings, void>({
      query: () => ({
        url: `/practice/billing-settings/`,
        method: "GET"
      })
    }),
    // Practices the current user can switch into (ENG-1458 / T3). Suno staff get
    // ALL practices, so this is paginated + searchable — pair with a debounced
    // search input on the switcher. Tolerates a flat array or a DRF
    // `{ results }` envelope.
    getAccessiblePractices: builder.query<
      AccessiblePractice[],
      AccessiblePracticesFilters | void
    >({
      query: (filters) => ({
        url: `/auth/users/me/accessible-practices/`,
        params: {
          search: filters?.search || undefined,
          limit: filters?.limit ?? 25,
          offset: filters?.offset ?? 0
        }
      }),
      transformResponse: (
        response: AccessiblePractice[] | { results?: AccessiblePractice[] }
      ): AccessiblePractice[] =>
        Array.isArray(response) ? response : (response?.results ?? [])
    })
    // NOTE: switch-practice is NOT an RTK Query endpoint — it must send
    // `credentials: "include"` so the backend's HttpOnly refresh_token cookie is
    // stored, which fetchBaseQuery can't do per-request. See
    // `use-switch-practice.hook.ts` for the credentialed direct fetch.
  })
});

const {
  useUpdatePracticeMutation,
  useGetPracticeBillingSettingsQuery: useGetPracticeBillingSettingsQueryBase,
  useGetAccessiblePracticesQuery,
  useLazyGetAccessiblePracticesQuery
} = practicesApi;

const DEFAULT_PRACTICE_BILLING_SETTINGS: PracticeBillingSettings = {
  credit_card_payment_enabled: true,
  subscription_payment_enabled: true,
  terminal_payment_enabled: true,
  online_payment_enabled: true,
  allow_payment_save: true
};

export const useGetPracticeBillingSettingsQuery = () =>
  useGetPracticeBillingSettingsQueryBase(undefined, {
    selectFromResult: ({ data, error, ...rest }) => {
      const is404Error = error && "status" in error && error.status === 404;
      return {
        data: is404Error ? DEFAULT_PRACTICE_BILLING_SETTINGS : data,
        error: is404Error ? undefined : error,
        ...rest
      };
    }
  });

export {
  useUpdatePracticeMutation,
  useGetAccessiblePracticesQuery,
  useLazyGetAccessiblePracticesQuery
};
